import moment from 'moment';
import type { PromoBreakConfig, PromoBreakLength, PromoFrequencyMode, Segment } from './types';

const FRAMES_PER_SECOND = 30;
const PAID_PROGRAMMING_FILLER_TARGET_FRAMES = 90 * FRAMES_PER_SECOND;

export const PAID_PROGRAMMING_FILLER_DURATION_ERROR =
    'No combination of your paid programming filler equals 90 seconds and some will be cut off.';

const durationToFrames = (duration: string): number | null => {
    const parts = String(duration).trim().split(':').map(Number);

    if (parts.length === 4 && parts.every(Number.isFinite)) {
        const [hours, minutes, seconds] = parts;
        return (hours * 3600 + minutes * 60 + seconds) * FRAMES_PER_SECOND;
    }

    const milliseconds = Number(duration);
    return Number.isFinite(milliseconds)
        ? Math.round((milliseconds / 1000) * FRAMES_PER_SECOND)
        : null;
};

const hasExactRepeatableDurationCombination = (durations: number[], target: number): boolean => {
    const reachable = Array<boolean>(target + 1).fill(false);
    reachable[0] = true;

    durations.forEach((duration) => {
        // Iterate upward so each selected filler can be reused as many times as needed.
        for (let total = duration; total <= target; total += 1) {
            reachable[total] = reachable[total] || reachable[total - duration];
        }
    });

    return reachable[target];
};

export const getPaidProgrammingFillerDurationError = (
    fillers: Pick<Segment, 'duration'>[]
): string | null => {
    if (!fillers.length) return null;
    const durations = fillers
        .map(({ duration }) => durationToFrames(duration))
        .filter((duration): duration is number =>
            duration !== null && duration > 0 && duration <= PAID_PROGRAMMING_FILLER_TARGET_FRAMES
        );

    return hasExactRepeatableDurationCombination(durations, PAID_PROGRAMMING_FILLER_TARGET_FRAMES)
        ? null
        : PAID_PROGRAMMING_FILLER_DURATION_ERROR;
};

export const generateEventId = (objId: string, start: Date, end: Date) => {
    return `${objId}_${moment(start).format('YYYY-MM-DD-HH:mm')}_${moment(end).format('YYYY-MM-DD-HH:mm')}`;
}
  
export const parseEventId = (eventId: string) => {
    const idParts = eventId.split('_');
    const end = idParts.pop();
    const start = idParts.pop();
    const objId = idParts.join('_');
    return {
        objId,
        seriesId: objId,
        start,
        end
    };
}
  
// --- Amagi OTA promo breaks --------------------------------------------------
// Mirrors planPromoBreakLengths in the export Lambda
// (lambda-master/amagi-ota-schedule-export/services/promo-breaks.js). It lives
// here so the modal can show the operator the same plan the scheduler will
// build — keep the two in step.

/**
 * The promo break lengths the modal offers, shortest first — the single source
 * of truth. Adding 20 or 60 here adds the checkbox, the saved-config filter and
 * the plan preview; the export Lambda already honours any length and matches
 * promos on exact duration, so nothing there needs touching.
 */
export const PROMO_BREAK_LENGTHS: PromoBreakLength[] = [15, 30];

export const PROMO_BREAKS_DEFAULT: PromoBreakConfig = {
    enabled: false,
    lengths: [15, 30],
    frequencyMode: 'breaks',
    frequency: 4,
};

/** Bounds for the frequency input, per unit. */
export const PROMO_FREQUENCY_LIMITS = {
    breaks: { min: 1, max: 20 },
    seconds: { min: 15, max: 900 },
} as const;

/** Frequency to fall back to when a saved value is missing or unusable. */
export const PROMO_FREQUENCY_DEFAULTS: Record<PromoFrequencyMode, number> = {
    breaks: 4,
    seconds: 60,
};

const longestFitting = (lengths: number[], budget: number): number | null => {
    let best: number | null = null;
    lengths.forEach((len) => {
        if (len <= budget && (best === null || len > best)) best = len;
    });
    return best;
};

/**
 * The promo break lengths wanted in ONE hour of programming, in order.
 *
 * "breaks" mode alternates through the selected lengths N times. "seconds" mode
 * follows the same alternation but stops before exceeding the budget, falling
 * back to the longest selected length that still fits when the next one in the
 * cycle no longer does.
 */
export const planPromoBreakLengths = (
    config: Pick<PromoBreakConfig, 'lengths' | 'frequencyMode' | 'frequency'> | null | undefined
): number[] => {
    if (!config) return [];
    const cycle = config.lengths
        .filter((length, index) => config.lengths.indexOf(length) === index)
        .sort((a, b) => a - b);
    const frequency = Math.floor(Number(config.frequency));
    if (!cycle.length || !Number.isFinite(frequency) || frequency <= 0) return [];

    if (config.frequencyMode === 'breaks') {
        const count = Math.min(frequency, PROMO_FREQUENCY_LIMITS.breaks.max);
        return Array.from({ length: count }, (_, i) => cycle[i % cycle.length]);
    }

    const plan: number[] = [];
    let remaining = frequency;
    let cursor = 0;
    while (plan.length < 60) {
        const preferred = cycle[cursor % cycle.length];
        const pick = preferred <= remaining ? preferred : longestFitting(cycle, remaining);
        if (pick === null) break;
        plan.push(pick);
        remaining -= pick;
        cursor++;
    }
    return plan;
};

/** "1 min 30 s" / "45 s" — seconds rendered the way the modal shows them. */
export const formatPromoSeconds = (totalSeconds: number): string => {
    if (totalSeconds <= 0) return '0 s';
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (!minutes) return `${seconds} s`;
    if (!seconds) return `${minutes} min`;
    return `${minutes} min ${seconds} s`;
};

/**
 * The hint under the frequency input: what the operator just asked for,
 * expressed in the other unit, plus the break sequence when it alternates.
 */
export const describePromoBreakPlan = (
    config: Pick<PromoBreakConfig, 'lengths' | 'frequencyMode' | 'frequency'> | null | undefined
): string => {
    const plan = planPromoBreakLengths(config);
    if (!plan.length) {
        return 'Pick at least one break length and a frequency to schedule promos.';
    }
    const total = plan.reduce((sum, seconds) => sum + seconds, 0);
    const breakCount = `${plan.length} break${plan.length === 1 ? '' : 's'}`;
    const alternates = plan.some((seconds) => seconds !== plan[0]);
    const sequence = alternates ? ` (${plan.map((s) => `${s} s`).join(', ')})` : '';
    return config?.frequencyMode === 'seconds'
        ? `${total} s of promos each hour = ${breakCount}${sequence}.`
        : `${breakCount} \u2248 ${formatPromoSeconds(total)} of promos each hour${sequence}.`;
};
